import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Facebook, Instagram, MapPin, Phone, Mail } from 'lucide-react';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSystemSettings();

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and mission */}
          <div>
            <Link to="/" className="flex items-center mb-4">
              <img
                src="/logo-white.svg"
                alt="Protecția Animalelor S1"
                className="h-8 w-auto"
              />
              <div className="ml-2">
                <span className="text-white font-display font-bold text-lg leading-none block">
                  Protecția
                </span>
                <span className="text-white font-display text-sm leading-none block">
                  Animalelor S1
                </span>
              </div>
            </Link>
            <p className="text-gray-300 mb-4">
              Lucrăm pentru a oferi o viață mai bună animalelor fără stăpân și pentru a găsi cămine permanente pentru toți prietenii noștri blănoși.
            </p>
            <div className="flex space-x-4">
              <a 
                href={settings?.facebookUrl || 'https://www.facebook.com/people/Protec%C8%9Bia-Animalelor-Sector-1/61572807361488/#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-accent-light transition-colors" 
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href={settings?.instagramUrl || 'https://www.instagram.com/protectiaanimalelor.sector1/'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-accent-light transition-colors" 
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Linkuri rapide</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-accent-light transition-colors">
                  Acasă
                </Link>
              </li>
              <li>
                <Link to="/caini" className="text-gray-300 hover:text-accent-light transition-colors">
                  Câinii noștri
                </Link>
              </li>
              <li>
                {/* <Link to="/doneaza" className="text-gray-300 hover:text-accent-light transition-colors">
                  Donează
                </Link> */}
              </li>
              <li>
                <Link to="/intrebari" className="text-gray-300 hover:text-accent-light transition-colors">
                  Întrebări frecvente
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-accent-light transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Adoption */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Adoptă un animal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/caini" className="text-gray-300 hover:text-accent-light transition-colors">
                  Adoptă
                </Link>
              </li>
              <li>
                {/* <Link to="/doneaza" className="text-gray-300 hover:text-accent-light transition-colors">
                  Donează
                </Link> */}
              </li>
              <li>
                <Link to="/intrebari" className="text-gray-300 hover:text-accent-light transition-colors">
                  Întrebări și răspunsuri
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contactează-ne</h3>
            <ul className="space-y-4">
              <li className="flex">
                <MapPin size={20} className="mr-2 flex-shrink-0 text-accent-light" />
                <span>{settings?.address || 'Sos. Odăi, nr. 3-5, Sector 1, București'}</span>
              </li>
              <li className="flex">
                <Phone size={20} className="mr-2 flex-shrink-0 text-accent-light" />
                <span>{settings?.phone || '+40 750 209 751'}</span>
              </li>
              <li className="flex">
                <Mail size={20} className="mr-2 flex-shrink-0 text-accent-light" />
                <span>{settings?.email || 'protectiaanimalelor@primarias1.ro'}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2" aria-label="Informări publice">
          <a
            href="https://infocons.ro/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between gap-4 rounded-lg border border-white/15 bg-white/10 p-4 text-white transition-colors hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-accent-light"
            aria-label="InfoCons - protecția consumatorilor, se deschide într-o filă nouă"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-16 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-md">
                <img
                  src="/logo-infocons.png"
                  alt=""
                  className="max-h-full w-full rounded-md object-contain"
                  aria-hidden="true"
                />
              </span>
              <span>
                <span className="block text-base font-bold">InfoCons</span>
                <span className="block text-sm text-gray-300">Protecția consumatorilor</span>
              </span>
            </span>
            <ExternalLink size={18} className="flex-shrink-0 text-accent-light" aria-hidden="true" />
          </a>

          <a
            href="http://www.112.ro/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between gap-4 rounded-lg border border-white/15 bg-white/10 p-4 text-white transition-colors hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-accent-light"
            aria-label="Situații de Urgență - Serviciul de urgență 112, se deschide într-o filă nouă"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-16 w-32 flex-shrink-0 items-center justify-center">
                <img
                  src="/logo-112.png"
                  alt=""
                  className="h-full w-auto object-contain"
                  aria-hidden="true"
                />
              </span>
              <span>
                <span className="block text-base font-bold">Situații de Urgență</span>
                <span className="block text-sm text-gray-300">Serviciul de urgență 112</span>
              </span>
            </span>
            <ExternalLink size={18} className="flex-shrink-0 text-accent-light" aria-hidden="true" />
          </a>
        </div>

        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} Protecția Animalelor Sector 1. Toate drepturile rezervate.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
              <Link to="/politica-confidentialitate" className="text-gray-400 hover:text-accent-light text-sm transition-colors">
                Politica de confidențialitate
              </Link>
              <Link to="/termeni-conditii" className="text-gray-400 hover:text-accent-light text-sm transition-colors">
                Termeni și condiții
              </Link>
              <Link to="/politica-cookie" className="text-gray-400 hover:text-accent-light text-sm transition-colors">
                Politica privind cookie-urile
              </Link>
              <Link to="/accesibilitate" className="text-gray-400 hover:text-accent-light text-sm transition-colors">
                Accesibilitate
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
